import { getLastPosition } from "@/components/GeoProvider";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Car, MapPin, ArrowRight, Navigation, Percent,
  CheckCircle, Banknote, Smartphone, ShieldCheck, Loader2, CloudRain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LocationPicker from "@/components/LocationPicker";
import RainSurgeBanner from "@/components/RainSurgeBanner";
import TaxiLiveMap from "@/components/TaxiLiveMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tarification Taxi WÔMA (synchronisée avec le backend /api/taxi/config)
// Forfait : 1000 FCFA (inclut le 1er km) + 200 FCFA / km supplémentaire
// Distance : OSRM temps réel (fallback haversine côté serveur)
// Arrondi : aux 100 FCFA supérieurs
// Commission plateforme : 10% fixe
const TAXI_BASE_FARE = 1000;
const TAXI_PER_KM = 200;

const PAYMENT_METHODS = [
  { id: "cash", name: "Cash", icon: Banknote, color: "#10B981" },
  { id: "wave", name: "Wave", icon: Smartphone, color: "#D4AF37" },
  { id: "orange_money", name: "Orange Money", icon: Smartphone, color: "#FF6600" },
];

export default function TaxiOrder({ user, onLogout }) {
  const navigate = useNavigate();
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupCoords, setPickupCoords] = useState(null);
  const _q = new URLSearchParams(window.location.search);
  const [dropoffAddress, setDropoffAddress] = useState(_q.get("dest_address") || "");
  const [dropoffCoords, setDropoffCoords] = useState(
    _q.get("dest_lat") ? { lat: Number(_q.get("dest_lat")), lng: Number(_q.get("dest_lng")) } : null
  );
  const [step, setStep] = useState(1); // 1=pickup, 2=dropoff, 3=récap+paiement
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentPhone, setPaymentPhone] = useState(user?.phone || "");
  const [submitting, setSubmitting] = useState(false);
  const [pricing, setPricing] = useState({
    distanceKm: 0,
    fareTotal: 0,
    commission: 0,
    driverNet: 0,
    etaMin: 0,
    source: null,         // "osrm" | "haversine"
    surge: 1.0,
    rainSurge: false,
    geometry: null,       // [[lat, lng], ...] si OSRM a renvoyé la polyline
  });
  const [pricingLoading, setPricingLoading] = useState(false);

  // Départ par défaut : position GPS captée à l'ouverture, puis affinée par un fix précis
  useEffect(() => {
    const last = getLastPosition();
    if (last && !pickupCoords) {
      setPickupCoords({ lat: last.lat, lng: last.lng });
      setPickupAddress((a) => a || "Ma position actuelle");
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setPickupCoords((c) => (c && !last ? c : { lat: pos.coords.latitude, lng: pos.coords.longitude }));
          setPickupAddress((a) => a || "Ma position actuelle");
        },
        () => {},
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ ENGINE DE CALCUL TEMPS RÉEL — OSRM backend ============
  // Debounced : relance l'appel à chaque changement de pickup/dropoff.
  // Backend = /api/taxi/calculate-fare → OSRM + surge météo + arrondi 100.
  useEffect(() => {
    const pLat = pickupCoords?.lat;
    const pLng = pickupCoords?.lng;
    const dLat = dropoffCoords?.lat;
    const dLng = dropoffCoords?.lng;
    const valid = [pLat, pLng, dLat, dLng].every((v) => typeof v === "number" && !Number.isNaN(v));
    if (!valid) {
      setPricing({ distanceKm: 0, fareTotal: 0, commission: 0, driverNet: 0, etaMin: 0, source: null, surge: 1.0, rainSurge: false, geometry: null });
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setPricingLoading(true);
      try {
        const res = await axios.post(`${API}/taxi/calculate-fare`, {
          pickup_latitude: pLat,
          pickup_longitude: pLng,
          delivery_latitude: dLat,
          delivery_longitude: dLng,
        });
        if (cancelled) return;
        const d = res.data || {};
        setPricing({
          distanceKm: Number(d.distance_km) || 0,
          fareTotal: Number(d.taxi_fare) || 0,
          commission: Number(d.platform_commission) || 0,
          driverNet: Number(d.courier_earning) || 0,
          etaMin: Number(d.eta_min) || 0,
          source: d.distance_source || null,
          surge: Number(d.surge_multiplier) || 1.0,
          rainSurge: !!d.rain_surge_applied,
          geometry: Array.isArray(d.geometry) ? d.geometry : null,
        });
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.status === 400
            ? "Destination trop proche ou identique au départ — choisissez une autre adresse"
            : "Impossible de calculer le tarif — vérifiez votre connexion");
        }
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [pickupCoords, dropoffCoords]);

  // ============ SOUMISSION ============
  const handleSubmit = async () => {
    if (!pickupCoords || !dropoffCoords) {
      toast.error("Veuillez sélectionner les deux adresses");
      return;
    }
    if (!user?.id) {
      toast.error("Connexion requise");
      navigate("/auth");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        order_type: "taxi",
        customer_id: user.id,
        customer_email: user.email || "",
        customer_name: user.full_name || "",
        customer_phone: user.phone || "",
        pickup_address: pickupAddress,
        pickup_latitude: pickupCoords.lat,
        pickup_longitude: pickupCoords.lng,
        delivery_address: dropoffAddress,
        delivery_latitude: dropoffCoords.lat,
        delivery_longitude: dropoffCoords.lng,
        payment_method: paymentMethod,
        payment_phone: paymentMethod !== "cash" ? paymentPhone : null,
        items: [],
        items_amount: 0,
        delivery_fee: pricing.fareTotal,
        platform_commission: pricing.commission,
        courier_earning: pricing.driverNet,
        total_amount: pricing.fareTotal,
        distance_km: pricing.distanceKm,
        notes: "Course Taxi · Tarif WÔMA (1000 F + 200 F/km)",
      };
      const res = await axios.post(`${API}/orders`, payload);
      const orderId = res.data.id;
      toast.success("Course commandée — recherche d'un chauffeur en cours…");
      navigate(`/tracking?id=${orderId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setSubmitting(false);
    }
  };

  // ============ RENDU ============
  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 font-sans antialiased pb-28">
      {/* Overlay plein écran pendant la création de la course — évite la page blanche */}
      {submitting && (
        <div
          className="fixed inset-0 z-[9999] bg-[#020617]/95 backdrop-blur-sm flex flex-col items-center justify-center p-6"
          data-testid="taxi-processing-overlay"
        >
          <div className="relative">
            <div className="w-20 h-20 rounded-full border-4 border-[#D4AF37]/20 border-t-[#D4AF37] animate-spin" />
            <Car className="w-8 h-8 text-[#D4AF37] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="mt-6 text-xl font-black uppercase tracking-tight text-white">
            Votre course est en cours de traitement
          </p>
          <p className="mt-2 text-sm text-[#D4AF37] font-bold uppercase tracking-widest">
            Recherche du chauffeur le plus proche…
          </p>
          <p className="mt-6 text-[11px] text-slate-400 max-w-sm text-center">
            Ne quittez pas cette page. Vous allez être redirigé dès qu'un chauffeur accepte.
          </p>
        </div>
      )}
      {/* HEADER */}
      <header className="bg-gradient-to-r from-[#003594] via-[#003594] to-[#001A4D] px-4 py-4 shadow-xl relative z-10 border-b border-[#D4AF37]/20">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-black/30 flex items-center justify-center border border-white/10 hover:border-[#D4AF37]/50 transition-colors"
            aria-label="Retour"
            data-testid="taxi-back-btn"
          >
            <ArrowRight className="w-5 h-5 text-white rotate-180" />
          </button>
          <div className="text-center">
            <h1
              className="text-base md:text-xl font-black italic tracking-wide text-[#D4AF37] uppercase"
              data-testid="taxi-page-title"
            >
              Taxi WÔMA
            </h1>
            <p className="text-[9px] md:text-[10px] text-white/70 font-bold tracking-[0.3em] uppercase">
              Course · Dakar, Sénégal
            </p>
          </div>
          <div className="bg-[#D4AF37] text-[#0F172A] px-2.5 py-1 rounded-lg font-black text-[10px] uppercase tracking-wider flex items-center gap-1 shadow-md">
            <Percent size={10} strokeWidth={3} />
            10% Commission
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-6 grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* COLONNE GAUCHE — Stepper + actions */}
        <div className="lg:col-span-3 space-y-5">
          <RainSurgeBanner />
          {/* STEPPER */}
          <div className="flex items-center gap-2">
            {[
              { n: 1, label: "Départ" },
              { n: 2, label: "Destination" },
              { n: 3, label: "Paiement" },
            ].map((s, idx) => (
              <div key={s.n} className="flex items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${
                    step >= s.n
                      ? "bg-[#D4AF37] text-[#0F172A]"
                      : "bg-slate-800 text-slate-500"
                  }`}
                  data-testid={`taxi-step-${s.n}`}
                >
                  {s.n}
                </div>
                <span
                  className={`ml-2 text-[10px] font-black uppercase tracking-widest ${
                    step >= s.n ? "text-[#D4AF37]" : "text-slate-500"
                  }`}
                >
                  {s.label}
                </span>
                {idx < 2 && <div className="flex-1 h-px bg-slate-800 mx-2" />}
              </div>
            ))}
          </div>

          {/* STEP 1 — PICKUP */}
          {step === 1 && (
            <section
              className="bg-[#0B1E2B] rounded-[2rem] p-5 border border-white/10 shadow-xl"
              data-testid="taxi-pickup-section"
            >
              <h2 className="text-sm font-black uppercase tracking-wider text-slate-300 mb-4 flex items-center gap-2">
                <MapPin size={16} className="text-[#D4AF37]" />
                Adresse de récupération
              </h2>
              <LocationPicker
                onLocationSelect={(loc) => {
                  setPickupAddress(loc.address || "");
                  setPickupCoords({ lat: loc.lat, lng: loc.lng });
                }}
                initialAddress={pickupAddress}
                initialPosition={pickupCoords}
              />
              <Button
                onClick={() => setStep(2)}
                disabled={!pickupCoords || !pickupAddress}
                className="mt-4 w-full bg-gradient-to-r from-[#003594] to-[#0046C6] hover:opacity-90 disabled:opacity-40 text-white py-5 rounded-full font-black text-xs uppercase tracking-[0.3em]"
                data-testid="taxi-next-step-1"
              >
                Suivant · Destination <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </section>
          )}

          {/* STEP 2 — DROPOFF */}
          {step === 2 && (
            <section
              className="bg-[#0B1E2B] rounded-[2rem] p-5 border border-white/10 shadow-xl"
              data-testid="taxi-dropoff-section"
            >
              <h2 className="text-sm font-black uppercase tracking-wider text-slate-300 mb-4 flex items-center gap-2">
                <Navigation size={16} className="text-rose-400" />
                Adresse de destination
              </h2>
              <LocationPicker
                onLocationSelect={(loc) => {
                  setDropoffAddress(loc.address || "");
                  setDropoffCoords({ lat: loc.lat, lng: loc.lng });
                }}
                initialAddress={dropoffAddress}
                initialPosition={dropoffCoords}
              />
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 rounded-full text-xs font-black uppercase tracking-widest"
                  data-testid="taxi-back-step-2"
                >
                  Retour
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!dropoffCoords || !dropoffAddress}
                  className="flex-1 bg-gradient-to-r from-[#003594] to-[#0046C6] hover:opacity-90 disabled:opacity-40 text-white py-5 rounded-full font-black text-xs uppercase tracking-[0.3em]"
                  data-testid="taxi-next-step-2"
                >
                  Suivant · Paiement
                </Button>
              </div>
            </section>
          )}

          {/* STEP 3 — PAYMENT + CONFIRM */}
          {step === 3 && (
            <section
              className="bg-[#0B1E2B] rounded-[2rem] p-5 border border-white/10 shadow-xl"
              data-testid="taxi-payment-section"
            >
              <h2 className="text-sm font-black uppercase tracking-wider text-slate-300 mb-4 flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-400" />
                Mode de paiement
              </h2>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {PAYMENT_METHODS.map((m) => {
                  const Icon = m.icon;
                  const active = paymentMethod === m.id;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setPaymentMethod(m.id)}
                      className={`p-3 rounded-2xl border-2 flex flex-col items-center gap-1.5 transition-all ${
                        active
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 scale-[1.02]"
                          : "border-white/10 bg-black/40 hover:border-white/30"
                      }`}
                      data-testid={`taxi-payment-${m.id}`}
                    >
                      <Icon size={20} style={{ color: m.color }} />
                      <span className="text-[10px] font-black uppercase tracking-widest text-white">
                        {m.name}
                      </span>
                    </button>
                  );
                })}
              </div>

              {paymentMethod !== "cash" && (
                <div className="mb-4">
                  <label className="text-[10px] font-black uppercase text-slate-400 block mb-1.5">
                    Téléphone Mobile Money
                  </label>
                  <input
                    type="tel"
                    value={paymentPhone}
                    onChange={(e) => setPaymentPhone(e.target.value)}
                    placeholder="77 XXX XX XX"
                    className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-3 text-sm font-bold text-white focus:outline-none focus:border-[#D4AF37]"
                    data-testid="taxi-payment-phone"
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="flex-1 rounded-full text-xs font-black uppercase tracking-widest"
                  data-testid="taxi-back-step-3"
                >
                  Retour
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || pricingLoading || !pickupCoords || !dropoffCoords || pricing.fareTotal <= 0}
                  className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 disabled:opacity-40 text-[#0F172A] py-5 rounded-full font-black text-xs uppercase tracking-[0.12em] sm:tracking-[0.3em] shadow-lg shadow-[#D4AF37]/30"
                  data-testid="taxi-submit-btn"
                >
                  {submitting ? "Envoi…" : pricingLoading ? "Calcul…" : "Commander la course"}
                  {!submitting && !pricingLoading && <CheckCircle className="w-4 h-4 ml-2" strokeWidth={3} />}
                </Button>
              </div>
            </section>
          )}
        </div>

        {/* COLONNE DROITE — Récapitulatif tarifaire LIVE */}
        <aside className="lg:col-span-2 space-y-5">
          <div
            className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-[2rem] p-5 border border-[#D4AF37]/30 shadow-xl sticky top-4"
            data-testid="taxi-pricing-summary"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-[#D4AF37] flex items-center justify-center">
                <Car className="w-6 h-6 text-[#0F172A]" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Estimation course
                </p>
                <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">
                  Tarif sans surprise
                </p>
              </div>
            </div>

            {/* Mini-carte OSRM temps réel : trajet département→destination */}
            <div className="mb-4">
              <TaxiLiveMap
                pickup={pickupCoords}
                dropoff={dropoffCoords}
                geometry={pricing.geometry}
                distanceSource={pricing.source}
                className="h-56"
              />
            </div>

            <div className="space-y-2 text-xs">
              <Row label="Forfait base" value={`${TAXI_BASE_FARE} F`} />
              <Row label="Tarif kilométrique" value={`${TAXI_PER_KM} F / km (après 1er km)`} />
              <Row
                label="Distance"
                value={
                  pricingLoading
                    ? "…"
                    : pricing.distanceKm > 0
                      ? `${pricing.distanceKm.toFixed(2)} km`
                      : "—"
                }
              />
              <Row
                label="Durée estimée"
                value={pricing.etaMin > 0 ? `~${pricing.etaMin} min` : "—"}
              />
              {pricing.source && (
                <Row
                  label="Trajet calculé"
                  value={pricing.source === "osrm" ? "Route OSRM temps réel" : "Vol d'oiseau (fallback)"}
                  dim
                />
              )}
              {pricing.rainSurge && (
                <div className="flex items-center gap-2 bg-sky-500/10 border border-sky-500/30 rounded-xl px-3 py-2 text-[11px] font-bold text-sky-300">
                  <CloudRain size={14} />
                  Mode Pluie actif · tarif ×{pricing.surge.toFixed(2)}
                </div>
              )}
              <div className="my-3 h-px bg-white/10" />
              <Row label="Commission plateforme (10%)" value={`-${pricing.commission.toLocaleString("fr-FR")} F`} dim />
              <Row label="Reversé au chauffeur" value={`${pricing.driverNet.toLocaleString("fr-FR")} F`} dim />
            </div>

            <div className="mt-4 p-4 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/40">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#D4AF37] mb-1 flex items-center gap-2">
                Prix garanti client
                {pricingLoading && <Loader2 size={11} className="animate-spin" />}
              </p>
              <p
                className="text-3xl font-black italic text-white tracking-tighter"
                data-testid="taxi-fare-total"
              >
                {pricing.fareTotal > 0 ? pricing.fareTotal.toLocaleString("fr-FR") : "—"}
                <span className="text-sm not-italic text-[#D4AF37] ml-2">FCFA</span>
              </p>
            </div>

            {pickupAddress && (
              <div className="mt-4 p-3 rounded-xl bg-black/30 border border-white/5">
                <p className="text-[9px] font-black uppercase tracking-widest text-emerald-400 mb-1">
                  Départ
                </p>
                <p className="text-xs font-bold text-white line-clamp-2">{pickupAddress}</p>
              </div>
            )}
            {dropoffAddress && (
              <div className="mt-2 p-3 rounded-xl bg-black/30 border border-white/5">
                <p className="text-[9px] font-black uppercase tracking-widest text-rose-400 mb-1">
                  Destination
                </p>
                <p className="text-xs font-bold text-white line-clamp-2">{dropoffAddress}</p>
              </div>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
}

const Row = ({ label, value, dim = false }) => (
  <div className="flex justify-between items-center">
    <span className={`text-[10px] font-bold uppercase tracking-widest ${dim ? "text-slate-500" : "text-slate-400"}`}>
      {label}
    </span>
    <span className={`text-xs font-black ${dim ? "text-slate-400" : "text-white"}`}>
      {value}
    </span>
  </div>
);
